import React, { FC, useMemo, useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { AgentThread } from '@dreamai/types';
import { ReactComponent as ArrowLeftIcon } from '../../../icons/arrow-left.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/campaign.svg';
import { ReactComponent as PlanIcon } from '../../../icons/plan.svg';
import { ReactComponent as ShareIcon } from '../../../icons/share.svg';
import { ReactComponent as ChatBubbleIcon } from '../../../icons/chat-bubble.svg';
import { useSidebarDrawer } from '../../../contexts/SidebarDrawerContext';
import { useThreadReads } from '../../../providers';
import ThreadItem from './ThreadItem';
import './sidebar-drawer.scss';

interface SidebarDrawerProps {
  onThreadSelect: (
    threadId: string,
    agentId?: string,
    readMarker?: string,
  ) => void;
}

const SidebarDrawer: FC<SidebarDrawerProps> = ({ onThreadSelect }) => {
  const { user } = useAuth0();
  const userId = user?.sub;
  const { openDrawer, setOpenDrawer, drawerThreads } = useSidebarDrawer();
  const [searchParams] = useSearchParams();
  const selectedThreadId = searchParams.get('thread_id');

  // Get unread threads from ThreadReadsProvider
  const { getAllUnreadThreads } = useThreadReads();
  const unreadThreads = getAllUnreadThreads();
  const unreadThreadIds = new Set(unreadThreads.map((t) => t.thread_id));

  // Create map of thread_id -> last_read_at
  const threadReadMap = useMemo(() => {
    return new Map(unreadThreads.map((t) => [t.thread_id, t.last_read_at]));
  }, [unreadThreads]);

  // Helper to create read marker: base64(userId:lastReadAt)
  const createReadMarker = useCallback(
    (threadId: string, userId: string | undefined): string | undefined => {
      if (!userId) return undefined;
      const lastReadAt = threadReadMap.get(threadId);
      if (!lastReadAt) return undefined;

      const data = JSON.stringify({ userId, lastReadAt });
      return btoa(data); // base64 encode
    },
    [threadReadMap],
  );

  // State for "show more" functionality
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // State for closing animation
  const [isClosing, setIsClosing] = useState(false);
  const [lastOpenDrawer, setLastOpenDrawer] = useState<string | null>(null);

  // Track the last open drawer for closing animation
  useEffect(() => {
    if (openDrawer) {
      setLastOpenDrawer(openDrawer);
      setIsClosing(false);
    }
  }, [openDrawer]);

  const closeDrawer = () => {
    setIsClosing(true);
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      setIsClosing(false);
      setOpenDrawer(null);
    }, 300); // Match animation duration
  };

  // Toggle group expansion (show more functionality)
  const toggleGroup = (groupName: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }
      return newSet;
    });
  };

  // Pluralize discussing object names
  const pluralize = (word: string): string => {
    // Don't pluralize special group names
    if (word === 'Side Chats' || word === 'Shared with me') return word;

    // Simple pluralization rules
    if (
      word.endsWith('s') ||
      word.endsWith('x') ||
      word.endsWith('z') ||
      word.endsWith('ch') ||
      word.endsWith('sh')
    ) {
      return word + 'es';
    } else if (word.endsWith('y') && !/[aeiou]y$/i.test(word)) {
      return word.slice(0, -1) + 'ies';
    } else {
      return word + 's';
    }
  };

  // Get icon for discussing object
  const getDiscussingObjectIcon = (
    discussingObject: string,
  ): React.ReactNode | null => {
    const singular = discussingObject.toLowerCase();

    if (singular === 'shared with me') {
      return <ShareIcon />;
    } else if (singular === 'campaign' || singular === 'campaigns') {
      return <CampaignIcon />;
    } else if (singular === 'plan' || singular === 'plans') {
      return <PlanIcon />;
    } else if (singular === 'side chats') {
      return <ChatBubbleIcon />;
    }

    // Default fallback for anything else
    return <ChatBubbleIcon />;
  };

  // Show drawer if it's open OR if it's closing (for animation)
  if (!openDrawer && !isClosing) return null;

  // Use the current or last open drawer name for rendering during closing animation
  const currentDrawer = openDrawer || lastOpenDrawer;
  if (!currentDrawer) return null;

  const threadsToShowPerGroup = 12;

  return (
    <div className={`sidebar-drawer-container ${isClosing ? 'closing' : ''}`}>
      <div className='drawer-header'>
        <button className='drawer-close' onClick={closeDrawer}>
          <ArrowLeftIcon />
        </button>
        <h5>{pluralize(currentDrawer)}</h5>
      </div>
      <div className='drawer-content'>
        <ul className='thread-list'>
          {drawerThreads
            ?.slice(
              0,
              expandedGroups.has(currentDrawer)
                ? drawerThreads.length
                : threadsToShowPerGroup,
            )
            .map((thread) => {
              const isUnread = unreadThreadIds.has(thread.id);
              return (
                <ThreadItem
                  key={thread.id}
                  thread={thread}
                  selectedThreadId={selectedThreadId}
                  onThreadSelect={onThreadSelect}
                  discussingObject={currentDrawer}
                  isUnread={isUnread}
                  createReadMarker={(threadId) =>
                    createReadMarker(threadId, userId)
                  }
                  getDiscussingObjectIcon={getDiscussingObjectIcon}
                  onSelect={closeDrawer}
                />
              );
            })}
        </ul>
        {drawerThreads && drawerThreads.length > threadsToShowPerGroup && (
          <div className='show-more-container'>
            <button
              className='show-more-button'
              onClick={() => toggleGroup(currentDrawer)}
            >
              {expandedGroups.has(currentDrawer)
                ? `Hide older ${pluralize(currentDrawer).toLowerCase()}`
                : `Show older ${pluralize(currentDrawer).toLowerCase()}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarDrawer;
